import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from './components/ui/sonner';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Components
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import NewClient from './pages/NewClient';
import AppointmentsEnhanced from './pages/AppointmentsEnhanced';
import AppointmentCreatePage from './pages/AppointmentCreatePage';
import Products from './pages/Products';
import Providers from './pages/Providers';
import ProviderForm from './pages/ProviderForm';
import Purchases from './pages/Purchases';
import PurchaseCreditNotes from './pages/PurchaseCreditNotes';
import CreatePurchaseCreditNote from './pages/CreatePurchaseCreditNote';
import PurchaseNew from './pages/PurchaseNew';
import PurchaseDetail from './pages/PurchaseDetail';
import PriceLists from './pages/PriceLists';
import CashRegister from './pages/CashRegister';
import CashRegisterDetail from './pages/CashRegisterDetail';
import FinancialPosition from './pages/FinancialPosition';
import ResumenDiario from './pages/ResumenDiario';
import Reports from './pages/Reports';
import OperatingExpenses from './pages/OperatingExpenses';
import ActiveServicesArchive from './pages/ActiveServicesArchive';
import Workshop from './pages/Workshop';
import MonitorTaller from './pages/MonitorTaller';
import UploadPhotos from './pages/UploadPhotos';
import DiagnosisPhotos from './pages/DiagnosisPhotos';
import ReceptionPhotos from './pages/ReceptionPhotos';
import VehicleReceptionPage from './pages/VehicleReception';
import VehicleReceptions from './pages/VehicleReceptions';
import SubirMaterial from './pages/SubirMaterial';
import Budgets from './pages/Budgets';
import Remitos from './pages/Remitos';
import Diagnostics from './pages/Diagnostics';
import DiagnosticChecklist from './pages/DiagnosticChecklist';
import ServiceStatus from './pages/ServiceStatus';
import PublicDiagnosis from './pages/PublicDiagnosis';
import PublicVehicleBook from './pages/PublicVehicleBook';
import OperatorHome from './pages/OperatorHome';
import VehicleHistory from './pages/VehicleHistory';
import Discounts from './pages/Discounts';
import CreateDiscount from './pages/CreateDiscount';
import CreateCoupon from './pages/CreateCoupon';
import Invoices from './pages/Invoices';
import CreditNotes from './pages/CreditNotes';
import CreateCreditNote from './pages/CreateCreditNote';
import CurrentAccounts from './pages/CurrentAccounts';
import CurrentAccountDetail from './pages/CurrentAccountDetail';
import Loyalty from './pages/Loyalty';
import Supplies from './pages/Supplies';
import SupplyAreas from './pages/SupplyAreas';
import Assets from './pages/Assets';
import AssetDetail from './pages/AssetDetail';
import PublicAsset from './pages/PublicAsset';
import SupplyForm from './pages/SupplyForm';
import SupplyView from './pages/SupplyView';
import SupplyRequests from './pages/SupplyRequests';
import Settings from './pages/Settings';
import VisionDataset from './pages/VisionDataset';
import CompanyInfo from './pages/CompanyInfo';
import ProductForm from './pages/ProductForm';
import ProductMarkup from './pages/ProductMarkup';
import ProductView from './pages/ProductView';
import ProductCostAdjustments from './pages/ProductCostAdjustments';
import Users from './pages/Users';
import UserEdit from './pages/UserEdit';
import Roles from './pages/Roles';
import Skills from './pages/Skills';
import PaymentMethods from './pages/PaymentMethods';
import FinancialAccounts from './pages/FinancialAccounts';
import AssignAccounts from './pages/AssignAccounts';
import CurrentAccountPay from './pages/CurrentAccountPay';
import EmailConfig from './pages/EmailConfig';
import Alerts from './pages/Alerts';
import CashChecks from './pages/CashChecks';
import CheckNew from './pages/CheckNew';
import RemitoPDFView from './components/RemitoPDFView';
import { ConfirmDialogHost } from './components/ConfirmDialog';
import { PromptDialogHost } from './components/PromptDialog';
import InteractiveBudgetView from './pages/InteractiveBudgetView';
import KapsoSettings from './pages/KapsoSettings';
import Profile from './pages/Profile';
import ProductStock from './pages/ProductStock';
import SupplyStock from './pages/SupplyStock';
import Backups from './pages/Backups';
import Marketing from './pages/Marketing';
import Goals from './pages/Goals';
import Tasks from './pages/Tasks';
import NewTask from './pages/NewTask';
import WesOperator from './pages/WesOperator';
import WesGeneral from './pages/WesGeneral';
import ManualsPage from './pages/Manuals';
import StaffPaymentsPage from './pages/StaffPayments';
import ContentCalendarPage from './pages/ContentCalendar';
import ContentIdeas from './pages/ContentIdeas';
import NewContentIdea from './pages/NewContentIdea';
import GoogleCalendarView from './pages/GoogleCalendarView';
import ContentMedia from './pages/ContentMedia';
import Chat from './pages/Chat';
import { WHATSAPP_CHAT_ENABLED } from './config/modules';
import Notes from './pages/Notes';
import Fleets from './pages/Fleets';
import FleetDetail from './pages/FleetDetail';
import FleetVehicleDetail from './pages/FleetVehicleDetail';
import FleetCalendar from './pages/FleetCalendar';
import FleetReports from './pages/FleetReports';
import ContactPublic from './pages/ContactPublic';
import ReviewRedirect from './pages/ReviewRedirect';
import Reviews from './pages/Reviews';
import ReviewsConfig from './pages/ReviewsConfig';
import CouponRules from './pages/CouponRules';

const TitleManager: React.FC = () => {
  const location = useLocation();

  React.useEffect(() => {
    if (location.pathname.startsWith('/presupuesto/')) {
      document.title = 'Presupuesto ROCAM';
      return;
    }

    if (location.pathname === '/contacto') {
      document.title = 'Contacto ROCAM';
      return;
    }

    document.title = 'Sistema ROCAM';
  }, [location.pathname]);

  return null;
};

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// logout() deja anotada la hora: si la sesión se acaba de cerrar a mano, el próximo
// inicio de sesión no vuelve a la página donde estaba el que salió
const volverTrasLogin = {
  cerroSesion(): boolean {
    try {
      const cuando = Number(sessionStorage.getItem('rocam-cerro-sesion')) || 0;
      return Date.now() - cuando < 5000;
    } catch {
      return false;
    }
  }
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, hasPermission } = useAuth();
  const location = useLocation();

  React.useEffect(() => {
    console.log('ProtectedRoute - location:', location.pathname, 'loading:', loading, 'user:', !!user);
  }, [location.pathname, loading, user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    console.log('ProtectedRoute - No user, redirecting to login');
    // Se guarda a dónde iba (por ejemplo, al tocar una notificación con la sesión vencida)
    // para volver ahí después de iniciar sesión. Si cerró sesión a mano, no: el que entre
    // después puede ser otra persona.
    const destino = `${location.pathname}${location.search}${location.hash}`;
    const cerroSesion = volverTrasLogin.cerroSesion();
    const volver = !cerroSesion && destino !== '/' && destino !== '/dashboard' ? `?returnTo=${encodeURIComponent(destino)}` : '';
    return <Navigate to={`/login${volver}`} replace state={{ from: location }} />;
  }

  if (location.pathname === '/dashboard' && !hasPermission('dashboard')) {
    // Operarios aterrizan en su inicio (su día: taller + checklists + comisiones)
    if (hasPermission('wes_operator')) return <Navigate to="/inicio" replace />;
    if (hasPermission('appointments')) return <Navigate to="/appointments-enhanced" replace />;
    if (hasPermission('wes_general')) return <Navigate to="/wes/general" replace />;
    if (hasPermission('manage_content_calendar')) return <Navigate to="/content-calendar" replace />;
    if (hasPermission('tasks')) return <Navigate to="/tasks" replace />;
    if (hasPermission('clients')) return <Navigate to="/clients" replace />;
    return <Navigate to="/alerts" replace />;
  }

  if (location.pathname.startsWith('/wes/operator') && !hasPermission('wes_operator')) {
    if (hasPermission('wes_general')) return <Navigate to="/wes/general" replace />;
    if (hasPermission('appointments')) return <Navigate to="/appointments-enhanced" replace />;
    return <Navigate to="/alerts" replace />;
  }

  if (location.pathname.startsWith('/wes/general') && !hasPermission('wes_general')) {
    if (hasPermission('wes_operator')) return <Navigate to="/wes/operator" replace />;
    if (hasPermission('appointments')) return <Navigate to="/appointments-enhanced" replace />;
    return <Navigate to="/alerts" replace />;
  }

  if (location.pathname.startsWith('/alerts') && !hasPermission('alerts')) {
    if (hasPermission('dashboard')) return <Navigate to="/dashboard" replace />;
    if (hasPermission('appointments')) return <Navigate to="/appointments-enhanced" replace />;
    if (hasPermission('wes_operator')) return <Navigate to="/wes/operator" replace />;
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// RequirePermission: bloquea rutas si el usuario no tiene el permiso requerido
const RequirePermission: React.FC<{ permission: string; children: React.ReactNode }> = ({ permission, children }) => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  if (!hasPermission(permission)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center px-4">
        <div className="rounded-full bg-destructive/10 p-6">
          <svg className="h-12 w-12 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-foreground">Acceso no autorizado</h2>
          <p className="text-muted-foreground max-w-sm">No tenés permiso para acceder a esta sección. Contactá al administrador si creés que es un error.</p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="mt-2 inline-flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          ← Volver
        </button>
      </div>
    );
  }
  return <>{children}</>;
};

// Public Route Component (redirects to dashboard if already logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider>
          <Router>
            <div className="App">
            <TitleManager />
            <Toaster position="top-right" richColors />
            <ConfirmDialogHost />
            <PromptDialogHost />
            
            <Routes>
              {/* Public Routes */}
              <Route 
                path="/login" 
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                } 
              />
              
              {/* PDF Route - Protected but accessible from backend */}
              <Route 
                path="/remito/preview-print/:id" 
                element={<RemitoPDFView />} 
              />
              {/* Public interactive budget */}
              <Route path="/presupuesto/:token" element={<InteractiveBudgetView />} />
              {/* Link de reseña de Google con tracking de click */}
              <Route path="/r/:token" element={<ReviewRedirect />} />
              {/* Informe de diagnóstico para el cliente (link público) */}
              <Route path="/d/:token" element={<PublicDiagnosis />} />
              <Route path="/v/:token" element={<PublicVehicleBook />} />
              <Route path="/contacto" element={<ContactPublic />} />

              {/* Workshop monitor (kiosko) y upload de fotos por QR - sin layout/sidebar */}
              <Route path="/taller/monitor/:stationKey" element={<MonitorTaller />} />
              <Route path="/upload-photos/:token" element={<UploadPhotos />} />
              <Route path="/fotos-diagnostico/:token" element={<DiagnosisPhotos />} />
              <Route path="/fotos-acta/:token" element={<ReceptionPhotos />} />
              <Route path="/subir-material/:token" element={<SubirMaterial />} />
              <Route path="/activo/:token" element={<PublicAsset />} />

              {/* Protected Routes */}
              <Route 
                path="/" 
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="clients" element={<RequirePermission permission="clients"><Clients /></RequirePermission>} />
                <Route path="clients/new" element={<RequirePermission permission="clients"><NewClient /></RequirePermission>} />
                <Route path="clients/:id/edit" element={<RequirePermission permission="clients"><NewClient /></RequirePermission>} />
                {/* Chat de WhatsApp: módulo apagado (config/modules.ts). La ruta
                    queda redirigiendo para que un link viejo no muestre pantalla vacía */}
                <Route path="chat" element={WHATSAPP_CHAT_ENABLED
                  ? <RequirePermission permission="whatsapp"><Chat /></RequirePermission>
                  : <Navigate to="/dashboard" replace />} />
                <Route path="notes" element={<Notes />} />
                <Route path="fleets" element={<Fleets />} />
                <Route path="fleets/:id" element={<FleetDetail />} />
                <Route path="fleets/:id/vehicles/:fleetVehicleId" element={<FleetVehicleDetail />} />
                <Route path="fleets/calendar" element={<FleetCalendar />} />
                <Route path="fleets/reports" element={<FleetReports />} />
                <Route path="appointments-enhanced" element={<AppointmentsEnhanced />} />
                <Route path="appointments-enhanced/new" element={<RequirePermission permission="create_appointments"><AppointmentCreatePage /></RequirePermission>} />
                <Route path="products" element={<RequirePermission permission="reports"><Products /></RequirePermission>} />
                <Route path="assets" element={<RequirePermission permission="manage_assets"><Assets /></RequirePermission>} />
                <Route path="assets/:id" element={<RequirePermission permission="manage_assets"><AssetDetail /></RequirePermission>} />
                <Route path="products/markup" element={<ProductMarkup />} />
                <Route path="products/cost-adjustments" element={<ProductCostAdjustments />} />
                <Route path="products/new" element={<ProductForm />} />
                <Route path="products/:id/view" element={<ProductView />} />
                <Route path="products/:id/edit" element={<ProductForm />} />
                <Route path="products/stock" element={<ProductStock />} />
                <Route path="providers" element={<Providers />} />
                <Route path="providers/new" element={<ProviderForm />} />
                <Route path="providers/:id/edit" element={<ProviderForm />} />
                <Route path="purchases" element={<Purchases />} />
                <Route path="purchases/credit-notes" element={<PurchaseCreditNotes />} />
                <Route path="purchases/credit-notes/new" element={<CreatePurchaseCreditNote />} />
                <Route path="purchases/new" element={<PurchaseNew />} />
                <Route path="purchases/:id/view" element={<PurchaseDetail />} />
                <Route path="purchases/:id/edit" element={<PurchaseNew />} />
                <Route path="price-lists" element={<PriceLists />} />
                <Route path="cash-register" element={<CashRegister />} />
                <Route path="finanzas/posicion" element={<FinancialPosition />} />
                <Route path="resumen-diario" element={<RequirePermission permission="reports"><ResumenDiario /></RequirePermission>} />
                <Route path="cash-register/checks/new" element={<CheckNew />} />
                <Route path="cash-register/checks/:id/edit" element={<CheckNew />} />
                <Route path="cash-register/checks" element={<CashChecks />} />
                <Route path="cash-register/:accountId" element={<CashRegisterDetail />} />
                <Route path="operating-expenses" element={<OperatingExpenses />} />
                <Route path="reports" element={<Reports />} />
                <Route path="reviews" element={<RequirePermission permission="reports"><Reviews /></RequirePermission>} />
                <Route path="reviews/config" element={<RequirePermission permission="reports"><ReviewsConfig /></RequirePermission>} />
                <Route path="coupon-rules" element={<RequirePermission permission="reports"><CouponRules /></RequirePermission>} />
                {/* Redirección: la página vieja "Servicios Activos" se reemplazó por /taller */}
                <Route path="active-services" element={<Navigate to="/taller" replace />} />
                <Route path="active-services/archive" element={<ActiveServicesArchive />} />
                <Route path="taller" element={<Workshop />} />
                <Route path="workshop" element={<Workshop />} />
                <Route path="budgets" element={<Budgets />} />
                <Route path="budgets/new" element={<Budgets />} />
                <Route path="remitos" element={<Remitos />} />
                <Route path="remitos/new" element={<Remitos />} />
                <Route path="diagnostics/new" element={<Diagnostics />} />
                <Route path="diagnostics/:id/checklist" element={<DiagnosticChecklist />} />
                <Route path="actas" element={<VehicleReceptions />} />
                <Route path="actas/:id" element={<VehicleReceptionPage />} />
                <Route path="diagnostics" element={<Diagnostics />} />
                <Route path="service-status" element={<ServiceStatus />} />
                <Route path="inicio" element={<OperatorHome />} />
                <Route path="vehicles/:vehicleId/history" element={<VehicleHistory />} />
                <Route path="discounts" element={<Navigate to="/loyalty" replace />} />
                <Route path="discounts/new" element={<CreateDiscount />} />
                <Route path="discounts/:id/edit" element={<CreateDiscount />} />
                <Route path="discounts/coupons/new" element={<CreateCoupon />} />
                <Route path="discounts/coupons/:id/edit" element={<CreateCoupon />} />
                <Route path="invoices" element={<Invoices />} />
                <Route path="notas-credito" element={<CreditNotes />} />
                <Route path="notas-credito/nueva" element={<CreateCreditNote />} />
                <Route path="notas-credito/nueva/:invoiceId" element={<CreateCreditNote />} />
                <Route path="supplies" element={<Supplies />} />
                <Route path="supplies/stock" element={<SupplyStock />} />
                <Route path="supplies/new" element={<SupplyForm />} />
                <Route path="supplies/:id/edit" element={<SupplyForm />} />
                <Route path="supplies/:id/view" element={<SupplyView />} />
                <Route path="supply-requests" element={<SupplyRequests />} />
                <Route path="settings/areas" element={<SupplyAreas />} />
                <Route path="settings/company" element={<CompanyInfo />} />
                <Route path="settings/whatsapp" element={<KapsoSettings />} />
                <Route path="backups" element={<Backups />} />
                <Route path="goals" element={<Goals />} />
                <Route path="tasks" element={<Tasks />} />
                <Route path="tasks/new" element={<NewTask />} />
                <Route path="tasks/:id/edit" element={<NewTask />} />
                <Route path="wes/operator" element={<WesOperator />} />
                <Route path="wes/general" element={<WesGeneral />} />
                <Route path="manuals" element={<ManualsPage />} />
                <Route path="staff-payments" element={<StaffPaymentsPage />} />
                <Route path="staff-payments/reglas" element={<StaffPaymentsPage />} />
                <Route path="staff-payments/empleado/:empId" element={<StaffPaymentsPage />} />
                <Route path="content-calendar" element={<ContentCalendarPage />} />
                <Route path="content-ideas" element={<ContentIdeas />} />
                <Route path="content-ideas/new" element={<NewContentIdea />} />
                <Route path="content-ideas/:id/edit" element={<NewContentIdea />} />
                <Route path="content-events" element={<GoogleCalendarView />} />
                <Route path="content-media" element={<ContentMedia />} />
                <Route path="current-accounts" element={<CurrentAccounts />} />
                <Route path="current-accounts/pay" element={<CurrentAccountPay />} />
                <Route path="current-accounts/:accountId" element={<CurrentAccountDetail />} />
                <Route path="loyalty" element={<Loyalty />} />
                <Route path="alerts" element={<Alerts />} />
                <Route path="settings" element={<Settings />} />
                <Route path="vision-dataset" element={<VisionDataset />} />
                <Route path="profile" element={<Profile />} />
                <Route path="users" element={<Users />} />
                <Route path="users/new" element={<UserEdit />} />
                <Route path="users/:id/edit" element={<UserEdit />} />
                <Route path="roles" element={<Roles />} />
                <Route path="skills" element={<Skills />} />
                <Route path="payment-methods" element={<PaymentMethods />} />
                <Route path="cuentas" element={<RequirePermission permission="reports"><FinancialAccounts /></RequirePermission>} />
                <Route path="finanzas/asignar-cuentas" element={<RequirePermission permission="reports"><AssignAccounts /></RequirePermission>} />
                <Route path="marketing" element={<Marketing />} />
                <Route path="email-config" element={<EmailConfig />} />
              </Route>
              
              {/* 404 Route */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </div>
        </Router>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App; 
